import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Sparkles, ArrowRight } from "lucide-react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { useCopilotStore } from "@/store/copilot.store"
import { useAuthStore } from "@/store/auth.store"
import { resolveCopilotContext } from "@/lib/copilot/context"
import { SIGNUP_PATH } from "@/lib/copilot/context"
import { GirlAvatar } from "./GirlAvatar"

/**
 * Context-aware greeting bubble. Shows a cloud-style message with quick-reply
 * chips that match the current page (URL context). The bubble appears a moment
 * after the page loads and auto-dismisses, but reappears when the route
 * changes or after a new chat is started.
 */
export function WelcomeTooltip() {
  const location = useLocation()
  const navigate = useNavigate()
  const isOpen = useCopilotStore((s) => s.isOpen)
  const isTooltipDismissed = useCopilotStore((s) => s.isTooltipDismissed)
  const setTooltipDismissed = useCopilotStore((s) => s.setTooltipDismissed)
  const toggleOpen = useCopilotStore((s) => s.toggleOpen)
  const send = useCopilotStore((s) => s.send)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [visible, setVisible] = useState(false)

  const search = useMemo(() => {
    const raw = location.search as Record<string, unknown>
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(raw ?? {})) if (typeof v === "string") out[k] = v
    return out
  }, [location.search])

  const context = useMemo(
    () => resolveCopilotContext([{ type: "url", pathname: location.pathname, search }], { authenticated: isAuthenticated }),
    [location.pathname, search, isAuthenticated]
  )

  /*
  const routeKey = `${location.pathname}${JSON.stringify(search)}`

  // Reset tooltip dismissal whenever the route or tab changes.
  useEffect(() => {
    setTooltipDismissed(false)
  }, [routeKey, setTooltipDismissed])
  */

  // Reset tooltip dismissal is removed so it only appears once per session.
  useEffect(() => {
    if (isOpen) setTooltipDismissed(true)
  }, [isOpen, setTooltipDismissed])

  useEffect(() => {
    if (isOpen || isTooltipDismissed) return

    // Greet the visitor shortly after the page loads (e.g. after a redirect).
    const timer = setTimeout(() => setVisible(true), 1200)
    // Auto dismiss after 10 seconds.
    const dismissTimer = setTimeout(() => {
      setVisible(false)
      setTooltipDismissed(true)
    }, 10000)
    // Close the bubble as soon as the user clicks anywhere or scrolls.
    const close = () => {
      setVisible(false)
      setTooltipDismissed(true)
    }
    window.addEventListener("click", close)
    window.addEventListener("scroll", close, true)

    return () => {
      clearTimeout(timer)
      clearTimeout(dismissTimer)
      window.removeEventListener("click", close)
      window.removeEventListener("scroll", close, true)
    }
  }, [isOpen, isTooltipDismissed, setTooltipDismissed])

  if (isOpen || isTooltipDismissed) return null

  const handleSuggestion = (prompt: string, isSignup: boolean) => {
    setVisible(false)
    setTooltipDismissed(true)
    if (isSignup) {
      navigate({ to: SIGNUP_PATH })
      return
    }
    toggleOpen()
    setTimeout(() => void send(prompt), 50)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed bottom-24 right-6 z-[35] w-[19rem] rounded-[1.4rem] border border-[#be001e]/15 bg-white p-0 shadow-[0_18px_50px_-12px_rgba(190,0,30,0.25)] text-left font-sans"
        >
          {/* Cloud bump + arrow pointing to the launcher */}
          <div className="absolute -bottom-1.5 right-8 size-3 rotate-45 rounded-br-[2px] border-r border-b border-[#be001e]/15 bg-white" />

          {/* Header */}
          <div className="flex items-start gap-2.5 px-4 pt-4">
            <div className="relative shrink-0">
              <span className="absolute -inset-1 rounded-full bg-[#be001e]/10 riya-ring" />
              <div className="relative flex size-10 items-center justify-center rounded-full bg-gradient-to-b from-white to-[#fff0ed] ring-1 ring-[#be001e]/20">
                <GirlAvatar size={36} talking />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-black uppercase tracking-wider text-[#be001e]">Enga AI</p>
              <p className="text-xs font-semibold text-slate-500">{context.label}</p>
            </div>
            <button
              onClick={() => setTooltipDismissed(true)}
              className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 cursor-pointer"
              aria-label="Dismiss greeting"
            >
              <X className="size-3.5" />
            </button>
          </div>

          {/* Greeting body */}
          <div className="px-4 pt-2.5 pb-3">
            <p className="text-sm font-bold leading-snug text-slate-800">{context.greeting}</p>
            {context.intro && (
              <p className="mt-1.5 text-xs font-medium leading-relaxed text-slate-600">
                {context.intro.replace(/\*\*/g, "")}
              </p>
            )}
          </div>

          {/* Quick replies */}
          <div className="space-y-1.5 px-3 pb-3.5">
            {context.suggestions.slice(0, 3).map((s) => {
              const isSignup = s.id.endsWith("-signup")
              const Icon = s.icon ?? (isSignup ? Sparkles : undefined)
              return (
                <button
                  key={s.id}
                  onClick={() => handleSuggestion(s.prompt, isSignup)}
                  className="group flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2 text-left text-xs font-semibold text-slate-700 transition-all hover:border-[#be001e]/40 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer"
                >
                  {Icon && <Icon className="size-3.5 shrink-0 text-[#e6202f]" />}
                  <span className="flex-1 leading-tight">{s.label}</span>
                  <ArrowRight className="size-3.5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#be001e]" />
                </button>
              )
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}